import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAccessToken } from '../services/authStorage';
import { interpretTimelineResponse } from '../utils/timelineResponse';
import { formatUTCTime, formatUTCDateTime } from '../utils/utcTime';
import './MultiDayTimeline.css';
import { reservationBadge } from '../utils/reservationStyle';

// Past-nights navigation helpers (shift a 'YYYY-MM-DD' by whole days, UTC-safe).
const shiftDateStr = (dateStr, deltaDays) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  return dt.toISOString().slice(0, 10);
};
const todayStr = () => new Date().toISOString().slice(0, 10);

/**
 * Fold the reservations overlapping a night into its schedule, in time order.
 *
 * Synthetic rows rather than a separate list: this table is read chronologically, and a block of
 * held time listed apart from the observations around it would misrepresent the night. A
 * reservation that blocks nothing -- cancelled before it began, so effective end <= start -- is
 * dropped rather than shown as a zero-length row.
 */
function withReservations(schedule, reservations, night) {
  const nightStart = new Date(night?.night_info?.twilight_evening_end).getTime();
  const nightEnd = new Date(night?.night_info?.twilight_morning_start).getTime();
  const rows = [...(schedule || [])];

  for (const r of reservations || []) {
    const startMs = new Date(r.start_time).getTime();
    const endMs = new Date(r.effective_blocking_end || r.end_time).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) continue;
    if (Number.isFinite(nightStart) && Number.isFinite(nightEnd)
        && (endMs <= nightStart || startMs >= nightEnd)) continue;
    rows.push({
      row_type: 'reserved',
      start_time: r.start_time,
      end_time: r.effective_blocking_end || r.end_time,
      duration_minutes: Math.round((endMs - startMs) / 60000),
      reservation: r,
    });
  }

  return rows.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
}

const MultiDayTimeline = () => {
  const navigate = useNavigate();
  const { observatoryId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const [expandedNights, setExpandedNights] = useState(new Set([0])); // First night expanded by default
  // Past-nights navigation: startDate = the window's anchor night (YYYY-MM-DD) or null for the
  // current window starting today.
  const [startDate, setStartDate] = useState(null);
  // Reservations are not part of the multi-day-timeline payload, so a night could look wide open
  // while the telescope was in fact held out of the dispatch pool for most of it.
  const [reservations, setReservations] = useState([]);

  // #1309b: the server returns a `computing` placeholder on a cold cache miss. This view has no
  // interval/WS refresh, so it must re-poll itself with backoff until the plan lands, keeping any
  // prior data on screen meanwhile.
  const [computing, setComputing] = useState(false);
  const rePollRef = useRef(null);
  const rePollAttemptRef = useRef(0);
  const fetchGenRef = useRef(0);
  const fetchDataRef = useRef(null);
  const cancelRePoll = useCallback(() => {
    if (rePollRef.current) { clearTimeout(rePollRef.current); rePollRef.current = null; }
  }, []);
  const scheduleRePoll = useCallback(() => {
    cancelRePoll();
    const delay = Math.min(20000, 5000 * 2 ** rePollAttemptRef.current); // 5s → 10s → 20s (capped)
    rePollAttemptRef.current += 1;
    rePollRef.current = setTimeout(() => { if (fetchDataRef.current) fetchDataRef.current(); }, delay);
  }, [cancelRePoll]);
  useEffect(() => () => cancelRePoll(), [cancelRePoll]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!observatoryId) return undefined;
    let cancelled = false;
    const loadReservations = async () => {
      try {
        const response = await fetch(
          `/api/v1/observatories/${observatoryId}/reservations?include_cancelled=1`,
          { headers: { Authorization: `Bearer ${getAccessToken()}` } },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        if (!cancelled) setReservations(payload?.data || []);
      } catch (e) {
        // Fail soft: no reserved rows is the pre-existing behaviour, not a broken page.
        if (!cancelled) setReservations([]);
      }
    };
    loadReservations();
    const onFocus = () => loadReservations();
    const onVisibility = () => { if (document.visibilityState === 'visible') loadReservations(); };
    const onMessage = (event) => {
      if (event?.data?.type === 'reservationsChanged') loadReservations();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('message', onMessage);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('message', onMessage);
    };
  }, [observatoryId]);

  useEffect(() => {
    if (observatoryId) {
      fetchData();
    }
    return () => cancelRePoll();
  }, [observatoryId, days, startDate]);

  const fetchData = async () => {
    const gen = ++fetchGenRef.current; // supersede any in-flight/scheduled fetch
    try {
      if (!data) setLoading(true); // only blank on a true first load
      const dateParam = startDate ? `&start_date=${startDate}` : '';
      const response = await fetch(
        `/api/v1/scheduler/multi-day-timeline?observatory_id=${observatoryId}&days=${days}${dateParam}`,
        { headers: { 'Authorization': `Bearer ${getAccessToken()}` } },
      );
      const body = await response.json().catch(() => null);
      if (gen !== fetchGenRef.current) return; // stale response — a newer fetch superseded it

      const parsed = interpretTimelineResponse(response.ok, body);

      if (parsed.status === 'computing') {
        // Cold cache: the scheduler is solving in the background. Keep prior data, show
        // "Computing…", and re-poll with backoff (this view has no other refresh trigger).
        setComputing(true);
        scheduleRePoll();
        return;
      }

      cancelRePoll();
      rePollAttemptRef.current = 0;
      setComputing(false);

      if (parsed.status === 'data') {
        setData(parsed.data);
        setError(null);
      } else if (parsed.status === 'empty') {
        setData(null); // renders the existing "No timeline data available" state
        setError(null);
      } else { // 'error' — keep prior data if we have it
        setError(parsed.message || 'Failed to load timeline');
      }
    } catch (err) {
      if (gen !== fetchGenRef.current) return;
      console.error('Error fetching multi-day timeline:', err);
      setError(err.message);
    } finally {
      if (gen === fetchGenRef.current) setLoading(false);
    }
  };
  fetchDataRef.current = fetchData;

  // Server timestamps are UTC but carry no timezone marker; parse them as UTC so the displayed
  // "UTC" times are not shifted by the viewer's local offset. See utils/utcTime.js.
  const formatTime = formatUTCTime;
  const formatDateTime = formatUTCDateTime;

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const toggleNight = (index) => {
    const newExpanded = new Set(expandedNights);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedNights(newExpanded);
  };

  const getPriorityClass = (priority) => {
    if (priority >= 8) return 'priority-high';
    if (priority >= 5) return 'priority-medium';
    return 'priority-low';
  };

  const getPriorityLabel = (priority) => {
    if (priority >= 8) return 'HIGH';
    if (priority >= 5) return 'MEDIUM';
    return 'LOW';
  };

  // Full-screen states only when there's nothing already on screen (#1309b): a computing
  // re-poll or a transient error must not wipe a schedule we're already showing.
  if (loading && !data) {
    return <div className="multi-day-timeline-container">Loading multi-day timeline...</div>;
  }

  if (computing && !data) {
    return (
      <div className="multi-day-timeline-container">
        ⏳ Computing schedule…{rePollAttemptRef.current > 3 ? ' (taking longer than usual)' : ''}
      </div>
    );
  }

  if (error && !data) {
    return <div className="multi-day-timeline-container error">Error: {error}</div>;
  }

  if (!data || !data.nights || data.nights.length === 0) {
    return <div className="multi-day-timeline-container">No timeline data available</div>;
  }

  return (
    <div className="multi-day-timeline-container">
      <div className="timeline-header">
        <h2>Multi-Day Observing Schedule - {data.observatory.name}</h2>
        <div className="timeline-controls">
          <button onClick={() => navigate(`/timeline/${observatoryId}`)}>
            📊 Single Night View
          </button>
          <label>
            Days to show:
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value))}>
              <option value="3">3 days</option>
              <option value="7">7 days</option>
              <option value="14">14 days</option>
            </select>
          </label>
          {/* Past-nights navigation: anchor the window on an earlier date */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            {startDate && <span style={{ fontSize: '0.8em', color: '#e67e22', fontWeight: 600 }} title="Window anchored in the past (static)">◐ past</span>}
            <button onClick={() => setStartDate(shiftDateStr((data?.nights?.[0]?.date) || todayStr(), -1))} title="Earlier night">◀</button>
            <input
              type="date"
              value={(data?.nights?.[0]?.date) || ''}
              max={todayStr()}
              onChange={(e) => setStartDate(e.target.value && e.target.value !== todayStr() ? e.target.value : null)}
              title="Jump the window to a date"
            />
            <button
              onClick={() => {
                const nd = shiftDateStr((data?.nights?.[0]?.date) || todayStr(), 1);
                setStartDate(nd >= todayStr() ? null : nd);
              }}
              disabled={!startDate}
              title="Later night"
            >▶</button>
            {startDate && (
              <button
                onClick={() => setStartDate(null)}
                title="Return to the current window"
                style={{ background: '#2ecc71', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer' }}
              >Current</button>
            )}
          </div>
          <button onClick={() => { cancelRePoll(); fetchData(); }}>🔄 Refresh</button>
          {computing && (
            <span style={{ marginLeft: 8, fontSize: '0.85em', color: '#888' }}>⏳ Computing…</span>
          )}
        </div>
      </div>

      <div className="observatory-info">
        <strong>Location:</strong> {data.observatory.location.latitude.toFixed(2)}°, {data.observatory.location.longitude.toFixed(2)}°
        {' | '}
        <strong>Timezone:</strong> {data.observatory.location.timezone}
      </div>

      {data.nights.map((night, nightIndex) => (
        <div key={nightIndex} className="night-section">
          <div
            className="night-header"
            onClick={() => toggleNight(nightIndex)}
            style={{ cursor: 'pointer' }}
          >
            <div className="night-header-content">
              <span className="expand-icon">
                {expandedNights.has(nightIndex) ? '▼' : '▶'}
              </span>
              <h3>Night {nightIndex + 1}: {night.date}</h3>
              <div className="night-summary">
                <span className="summary-item">
                  🌙 {(night.night_info.moon.illumination * 100).toFixed(0)}% Moon
                </span>
                <span className="summary-item">
                  ⏱ {night.night_info.total_dark_hours.toFixed(1)}h Dark Time
                </span>
                <span className="summary-item">
                  📊 {night.summary.total_observations} Observations
                </span>
                <span className="summary-item">
                  ⚡ {night.summary.utilization_percent.toFixed(0)}% Utilization
                </span>
                {night.summary.available_blocks > 0 && (
                  <span className="summary-item available">
                    ✓ {formatDuration(night.summary.available_time)} Available
                  </span>
                )}
              </div>
            </div>
          </div>

          {expandedNights.has(nightIndex) && (
            <div className="night-content">
              <div className="night-times">
                <strong>Dark Time (UTC):</strong> {formatTime(night.night_info.twilight_evening_end)} - {formatTime(night.night_info.twilight_morning_start)}
              </div>

              <table className="schedule-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Obs Type</th>
                    <th>Time (UTC)</th>
                    <th>Target</th>
                    <th>Duration</th>
                    <th>Filters</th>
                    <th>Priority</th>
                    <th>Alt°</th>
                    <th>Observer</th>
                  </tr>
                </thead>
                <tbody>
                  {withReservations(night.schedule, reservations, night).map((item, itemIndex) => {
                    if (item.row_type === 'reserved') {
                      const r = item.reservation;
                      return (
                        <tr key={`${nightIndex}-res-${r.id || r._id || itemIndex}`} className="row-available">
                          <td className="type-cell">
                            <span className="type-badge" style={reservationBadge(r).style}>
                              {reservationBadge(r).label}
                            </span>
                          </td>
                          <td>—</td>
                          <td>{formatDateTime(item.start_time)} - {formatDateTime(item.end_time)}</td>
                          <td><strong>{r.title || 'Observatory reservation'}</strong>{r.purpose ? ` — ${r.purpose}` : ''}</td>
                          <td>{formatDuration(item.duration_minutes)}</td>
                          <td>—</td>
                          <td>—</td>
                          <td>—</td>
                          <td>{r.contact_name || '—'}</td>
                        </tr>
                      );
                    }
                    if (item.row_type === 'available') {
                      return (
                        <tr key={`${nightIndex}-${itemIndex}`} className="row-available">
                          <td className="type-cell">
                            <span className="type-badge available">Available</span>
                          </td>
                          <td>—</td>
                          <td>{formatDateTime(item.start_time)} - {formatDateTime(item.end_time)}</td>
                          <td className="available-text">— Open Time Slot —</td>
                          <td>{formatDuration(item.duration_minutes)}</td>
                          <td>—</td>
                          <td>—</td>
                          <td>—</td>
                          <td>—</td>
                        </tr>
                      );
                    } else {
                      const obsType = item.observation_type || 'flexible';
                      const obsTypeLabel = obsType === 'fixed_time' ? 'Fixed' :
                                          obsType === 'time_based' ? 'Time-Based' :
                                          obsType === 'rise_to_set' ? 'Rise-to-Set' :
                                          'Flexible';
                      const obsTypeClass = obsType === 'fixed_time' ? 'obs-type-fixed' :
                                          obsType === 'time_based' ? 'obs-type-timebased' :
                                          obsType === 'rise_to_set' ? 'obs-type-risetoset' :
                                          'obs-type-flexible';

                      return (
                        <tr
                          key={`${nightIndex}-${itemIndex}`}
                          className="row-observation"
                          onClick={() => navigate(`/observations/${item.observation_id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="type-cell">
                            <span className="type-badge observation">Observation</span>
                          </td>
                          <td>
                            <span className={`obs-type-badge ${obsTypeClass}`}>
                              {obsTypeLabel}
                            </span>
                          </td>
                          <td>{formatDateTime(item.start_time)} - {formatDateTime(item.end_time)}</td>
                          <td>
                            <strong>{item.target_name}</strong>
                            {item.observation_type === 'time_based' && item.minimum_observing_time_minutes && (
                              <span className="time-based-indicator" title={`Time-based: ${item.minimum_observing_time_minutes}-${item.duration_minutes} min`}>
                                ⏱
                              </span>
                            )}
                            {item.transit_ingress && (
                              <span className="transit-indicator" title="Exoplanet Transit">
                                🪐
                              </span>
                            )}
                          </td>
                          <td>
                            {item.observation_type === 'time_based' && item.minimum_observing_time_minutes
                              ? `${item.minimum_observing_time_minutes}-${Math.round(item.duration_minutes)}m`
                              : formatDuration(item.duration_minutes)
                            }
                          </td>
                          <td>{item.metadata?.filters?.join(', ') || item.filters?.join(', ') || '—'}</td>
                          <td>
                            <span className={`priority-badge ${getPriorityClass(item.priority)}`}>
                              {getPriorityLabel(item.priority)}
                            </span>
                          </td>
                          <td>{item.altitude_at_start?.toFixed(0) || '?'}°</td>
                          <td>{item.observer_name || item.username || '—'}</td>
                        </tr>
                      );
                    }
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default MultiDayTimeline;
